/**
 * Shared regex patterns + table mappings for record identifiers
 * (e.g., `ORD-12345`, `SHP-2026-0001`) that can appear in chatbot
 * messages.
 *
 * Backend uses these to detect record-id filters in user queries
 * (`QueryIntentParserService.extractFilters`,
 * `ReferenceResolutionService` follow-up identifier propagation);
 * frontend uses them to render entity links in chatbot replies
 * (`apps/web-tms/src/features/chatbot/utils/chatbot.link-utils.ts`).
 *
 * Single source of truth — backend `RECORD_IDENTIFIER_PATTERNS` and
 * frontend `ENTITY_LINK_PATTERNS` both reconstruct their RegExp
 * objects from `CHATBOT_ENTITY_LINK_SPECS` so the prefix list, regex
 * shape, and tableName mapping cannot drift across stacks.
 *
 * Why specs (data) instead of shared `RegExp` objects: backend uses
 * `regex.test()` (stateless when no `g` flag) while frontend uses
 * `regex.exec()` in a loop (needs `g` flag, mutates `lastIndex`).
 * Sharing a single `gi`-flagged `RegExp` would corrupt the backend's
 * stateless `.test()` calls. Each side rebuilds its `RegExp` with the
 * flags it needs.
 *
 * Pattern shape: `(?:-\d+)+` matches one OR MORE dash-numeric segments
 * so production-shaped IDs like `ORD-2026-0001` (year + sequence)
 * match in full. A bare `\d+` would terminate the match at the first
 * dash and capture only `ORD-2026`.
 */
export interface ChatbotEntityLinkSpec {
  /** Human-friendly prefix (e.g., `ORD`, `SHP`) — surfaced in test invariants and error messages. */
  readonly prefix: string;
  /** Regex source string (no flags). Each consumer adds the flags it needs. */
  readonly source: string;
  /** Backend column name for the matched identifier. */
  readonly field: string;
  /** Database table name the identifier resolves to. */
  readonly tableName: string;
}

export const CHATBOT_ENTITY_LINK_SPECS: readonly ChatbotEntityLinkSpec[] = [
  {
    prefix: 'ORD',
    source: '#?\\bORD(?:-\\d+)+\\b',
    field: 'order_number',
    tableName: 'order_core',
  },
  {
    prefix: 'SHP',
    source: '#?\\bSHP(?:-\\d+)+\\b',
    field: 'shipment_number',
    tableName: 'shipment_core',
  },
  {
    prefix: 'REF',
    source: '#?\\bREF(?:-\\d+)+\\b',
    field: 'reference_number',
    tableName: 'shipment_core',
  },
  {
    prefix: 'INV',
    source: '#?\\bINV(?:-\\d+)+\\b',
    field: 'invoice_number',
    tableName: 'invoices',
  },
  {
    prefix: 'TRIP',
    source: '#?\\bTRIP(?:-\\d+)+\\b',
    field: 'trip_number',
    tableName: 'trips',
  },
  {
    prefix: 'TRP',
    source: '\\bTRP-\\d{8}-\\d{4,}\\b',
    field: 'trip_number',
    tableName: 'trips',
  },
  {
    prefix: 'RTE',
    source: '#?\\bRTE(?:-\\d+)+\\b',
    field: 'route_number',
    tableName: 'route_core',
  },
] as const;

export type ChatbotEntityLinkPrefix = (typeof CHATBOT_ENTITY_LINK_SPECS)[number]['prefix'];
